import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateYYYYMMDDhhmm, formatInteger, formatMoney } from '../../../packages/utils/numberFormatters'
import * as sharesOrUnits from '../../../utils/issues/sharesOrUnits.js'
import { FactsTable, FactsTableRow } from '../../components/FactsTable'
import InfoBlock from '../../components/InfoBlock'
import IssueTermsDiscountRow from '../IssueTermsDiscountRow'
import { EnrichedIssueEntity, IssueEntity } from '../../../types/IssueEntity'
import { DilutionRow } from './DilutionRow'
import { OverAllotmentSizeSecondaryRow } from './OverAllotmentSizeSecondaryRow'
import { OverAllotmentSizeRow } from './OverAllotmentSizeRow'
import { SecondaryOfferingRow } from './SecondaryOfferingRow'
import { PreMoneyRow } from './PreMoneyRow'
import { VolumeRow } from './VolumeRow'
import { PublicationDateRow } from './PublicationDateRow'
import { WarrantsRow } from './WarrantsRow'
import { AmountRow } from './AmountRow'
import { SubscribedUnitsRow } from './SubPriceUnitsRow'
import { SubPriceRow } from './SubPriceRow'
import { UnitContentRow } from './UnitContentRow'
import { ConditionalIssueRow } from './ConditionalIssueRow'
import { TermsRow } from './TermsRow'
import { ShareSplitRow } from './ShareSplitRow'
import { BonusIssueRow } from './BonusIssueRow'
import { SourceContentRow } from './SourceContentRow'
import { OutcomeDateRow } from './OutcomeDateRow'

interface IssueTermsProps {
    issue: IssueEntity
}

/**
 * Checks that the issue has a source of the given source type.
 */
const hasSourceOfType = (issue: IssueEntity, sourceType: string) =>
    issue.sources?.some((s) => s.sourceType === sourceType)

const addAbbRows = (issue: EnrichedIssueEntity, tableRows: ReactNode[], locale: string) => {
    const publicationDate = issue.sources.find((source) => source.sourceType === 'Publication')?.publishedDate
    const outcomeDate = issue.sources.find((source) => source.sourceType === 'Outcome')?.publishedDate
    if (issue.abbTarget) {
        tableRows.push(
            <FactsTableRow key={tableRows.length} label='Sökt belopp'>
                {formatMoney(issue.abbTarget, { locale, decimals: 0, currencySymbol: issue.currency })}
            </FactsTableRow>,
        )
    } else {
        tableRows.push(
            <FactsTableRow key={tableRows.length} label='Sökt antal aktier'>
                {formatInteger(issue.totalOfferedUnits, { locale })}
            </FactsTableRow>,
        )
    }

    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Tidpunkt för offentliggörande'>
            {formatDateYYYYMMDDhhmm(publicationDate)}
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Tidpunkt för stängning'>
            {formatDateYYYYMMDDhhmm(outcomeDate)}
        </FactsTableRow>,
    )
}

function Terms({ issue }: IssueTermsProps) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    let content: ReactNode = null

    if (issue) {
        const tableRows: ReactNode[] = []
        if (issue.type === 'ABB Directed issue') {
            addAbbRows(issue, tableRows, locale)
        }
        if (issue.type === 'Rights issue') {
            tableRows.push(<TermsRow key={tableRows.length} issue={issue} />)
        }
        if (issue.type === 'Rights issue') {
            tableRows.push(<ConditionalIssueRow key={tableRows.length} issue={issue} />)
        }
        if (['Rights issue', 'IPO'].includes(issue.type) && sharesOrUnits.isUnit(issue)) {
            tableRows.push(<UnitContentRow key={tableRows.length} issue={issue} />)
        }
        tableRows.push(<SubPriceRow key={tableRows.length} issue={issue} />)
        if (['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type) && issue.unitSubTotal) {
            tableRows.push(<SubscribedUnitsRow key={tableRows.length} issue={issue} />)
        }
        if (['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type)) {
            tableRows.push(<AmountRow key={tableRows.length} issue={issue} />)
        }
        if (['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type) && issue.warrants?.length) {
            tableRows.push(<WarrantsRow key={tableRows.length} issue={issue} />)
        }
        if (['Directed issue', 'Non-cash issue'].includes(issue.type)) {
            tableRows.push(<PublicationDateRow key={tableRows.length} issue={issue} />)
        }
        if (['Directed issue', 'Non-cash issue'].includes(issue.type) && hasSourceOfType(issue, 'Outcome')) {
            tableRows.push(<OutcomeDateRow key={tableRows.length} issue={issue} />)
        }

        if (!['Non-cash issue', 'IPO'].includes(issue.type)) {
            tableRows.push(<IssueTermsDiscountRow key={tableRows.length} issue={issue} />)
        }
        if (['Rights issue', 'IPO'].includes(issue.type)) {
            tableRows.push(<VolumeRow key={tableRows.length} issue={issue} />)
        }

        if (['Rights issue', 'IPO'].includes(issue.type) && issue.secondaryOffering) {
            tableRows.push(<SecondaryOfferingRow key={tableRows.length} issue={issue} />)
        }
        if (['Rights issue', 'IPO'].includes(issue.type) && issue.overAllotmentSize) {
            tableRows.push(<OverAllotmentSizeRow key={tableRows.length} issue={issue} />)
        }
        if (['Rights issue', 'IPO'].includes(issue.type) && issue.overAllotmentSizeSecondary) {
            tableRows.push(<OverAllotmentSizeSecondaryRow key={tableRows.length} issue={issue} />)
        }

        tableRows.push(<PreMoneyRow key={tableRows.length} issue={issue} />)

        if (issue.type !== 'IPO') {
            tableRows.push(<DilutionRow key={tableRows.length} issue={issue} />)
        }

        if ('IPO' !== issue.type) {
            // Show for all issue types except IPOs
            tableRows.push(<ShareSplitRow key={tableRows.length} issue={issue} />)
        }

        if ('IPO' !== issue.type) {
            // Show for all issue types except IPOs
            tableRows.push(<BonusIssueRow key={tableRows.length} issue={issue} />)
        }

        if (['Rights issue', 'Directed issue', 'Non-cash issue', 'IPO'].includes(issue.type)) {
            tableRows.push(<SourceContentRow key={tableRows.length} issue={issue} />)
        }

        content = <FactsTable rows={tableRows} />
    }
    return (
        <InfoBlock title='Villkor' loadingElement={!content}>
            {content}
        </InfoBlock>
    )
}

export default Terms
